
import { z } from "zod";
import { ProgramCategory } from "@/services/db/types"; // Import from types.ts

// Form schema
export const formSchema = z.object({
  id: z.coerce.number().optional(),
  name: z.string().min(2, { message: "First name must be at least 2 characters long" }),
  surname: z.string().optional(),
  fathersName: z.string().optional(),
  age: z.number().optional(),
  address: z.string().optional(),
  province: z.string().optional(),
  country: z.string().optional(),
  contactNumber: z.string().optional(),
  email: z.string().email({ message: "Please enter a valid email address" }).optional(),
  profileImage: z.string().optional(),
  qualificationLevel: z.enum([
    'Under 12th', 
    '12th Graduate', 
    'Diploma', 
    'Under Diploma', 
    'Bachelor Degree', 
    'Master Degree', 
    'PhD', 
    'Under PhD'
  ] as const).optional(),
  documents: z.array(
    z.object({
      name: z.string(),
      type: z.string(),
      uploadDate: z.string()
    })
  ).optional(),
  programs: z.array(
    z.object({
      category: z.string().min(1, { message: "Category is required" }),
      title: z.string().min(1, { message: "Program title is required" }),
      fee: z.coerce.number().min(0, { message: "Fee must be a non-negative number" }),
      enrolledAt: z.date({ required_error: "Enrollment date is required" }),
      duration: z.coerce.number().min(1, { message: "Duration must be at least 1 day" }),
      feeStatus: z.enum(['paid', 'pending', 'partial']),
      nextPaymentDate: z.date().optional(),
      completionDate: z.date().optional(),
    })
  ).min(1, { message: "At least one program is required" }),
  // Institution information fields
  institutionAddress: z.string().optional(),
  institutionPhone: z.string().optional(),
  institutionEmail: z.string().email({ message: "Please enter a valid institution email address" }).optional(),
  authorizedBy: z.string().optional(),
  policyNote: z.string().optional(),
  // Dates for tracking
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type StudentFormData = z.infer<typeof formSchema>;

// Helper to convert database Student to form data
export const convertStudentToFormData = (student: any): StudentFormData => {
  if (!student) return {} as StudentFormData;
  
  // Ensure documents have the correct format
  const documents = student.documents ? student.documents.map((doc: any) => ({
    name: doc.name,
    type: doc.type,
    uploadDate: typeof doc.uploadDate === 'object' 
      ? doc.uploadDate.toISOString() 
      : String(doc.uploadDate)
  })) : undefined;
  
  // Ensure programs have the correct date format
  const programs = student.programs ? student.programs.map((program: any) => ({
    ...program,
    category: String(program.category), // Ensure category is string for the form
    enrolledAt: typeof program.enrolledAt === 'string' ? new Date(program.enrolledAt) : program.enrolledAt,
    completionDate: program.completionDate ? 
      (typeof program.completionDate === 'string' ? new Date(program.completionDate) : program.completionDate) : undefined,
    nextPaymentDate: program.nextPaymentDate ? 
      (typeof program.nextPaymentDate === 'string' ? new Date(program.nextPaymentDate) : program.nextPaymentDate) : undefined,
  })) : [];
  
  return {
    ...student,
    id: typeof student.id === 'string' ? parseInt(student.id) : student.id,
    documents,
    programs
  };
};

// Helper to convert form data to database Student model
export const convertFormDataToStudent = (formData: StudentFormData): any => {
  // Process documents to ensure they match the DB format
  const documents = formData.documents ? formData.documents.map(doc => ({
    name: doc.name,
    type: doc.type,
    uploadDate: doc.uploadDate
  })) : undefined;
  
  // Process programs to ensure they have correct date and category formats
  const programs = formData.programs.map(program => ({
    ...program,
    category: program.category as ProgramCategory, // Cast to ProgramCategory for DB
    enrolledAt: typeof program.enrolledAt === 'object' ? program.enrolledAt.toISOString() : program.enrolledAt,
    completionDate: program.completionDate ? 
      (typeof program.completionDate === 'object' ? program.completionDate.toISOString() : program.completionDate) : undefined,
    nextPaymentDate: program.nextPaymentDate ? 
      (typeof program.nextPaymentDate === 'object' ? program.nextPaymentDate.toISOString() : program.nextPaymentDate) : undefined
  }));
  
  return {
    ...formData,
    documents,
    programs
  };
};
